import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMessage } from '../../contexts/MessageContext';
import axios from 'axios';
import Table from '../common/Table';
import Loading from '../common/Loading';
import { copyToClipboard } from '../../utils/clipboard';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const STATUS_COLORS = {
  draft:    'bg-gray-100 text-gray-700',
  sent:     'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  cancelled:'bg-red-100 text-red-600',
  converted:'bg-purple-100 text-purple-800',
};

const QuotesList = () => {
  const [quotes, setQuotes] = useState([]);
  const [load, setLoad] = useState(true);
  const { logout } = useAuth();
  const { showInfo } = useMessage();
  const navigate = useNavigate();

  const columns = [
    { key: 'quote_number', label: 'Quote #', sortable: true },
    {
      key: 'customer', label: 'Customer',
      render: (_, row) => row.Customer
        ? `${row.Customer.firstname || ''} ${row.Customer.lastname || ''}`.trim()
        : '—',
    },
    { key: 'total_amount', label: 'Total', type: 'currency' },
    {
      key: 'status', label: 'Status',
      render: value => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[value] || 'bg-gray-100 text-gray-700'}`}>
          {value || 'draft'}
        </span>
      ),
    },
    { key: 'due_date', label: 'Valid Until', type: 'date' },
    { key: 'createdAt', label: 'Created', type: 'date' },
    {
      key: 'share_link', label: 'Share',
      render: (_, row) => {
        const tok = row.share_token || row.ConvertedToInvoice?.share_token;
        if (!tok) return null;
        const url = `${window.location.origin}/quote/view/${tok}`;
        return (
          <button
            onClick={e => {
              e.stopPropagation();
              navigator.clipboard.writeText(url);
              showInfo('Customer link copied!');
            }}
            className="text-blue-600 hover:text-blue-800 text-xs underline whitespace-nowrap"
            title={url}
          >
            Copy Link
          </button>
        );
      },
    },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { logout(); navigate('/login'); return; }

    const fetchQuotes = async () => {
      try {
        const res = await axios.get(`${apiUrl}/invoice/quotes`, {
          headers: { 'x-auth-token': token },
        });
        setQuotes(res.data.data || []);
      } catch (err) {
        console.error('Error fetching quotes:', err);
      } finally {
        setLoad(false);
      }
    };
    fetchQuotes();
  }, [logout, navigate]);

  if (load) return <Loading />;

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-lg rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">All Quotes</h2>
        <button
          onClick={() => navigate('/quotes/new')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium text-sm"
        >
          + New Quote
        </button>
      </div>
      <Table
        data={quotes}
        columns={columns}
        defaultSortKey="createdAt"
        onRowClick={row => navigate(`/quotes/${row.uuid}`)}
        pageSize={10}
        paginated={true}
        rowKey="uuid"
      />
    </div>
  );
};

export default QuotesList;
